import axios from 'axios';
import { getAuth } from './auth.service';

const UNAUTH = { status: false, unauthenticated: true };

// Safety net so no request can hang forever; callers with a tighter or looser need pass `timeout` in config.
export const DEFAULT_TIMEOUT_MS = 120000;

const isTimeout = (ex) => ex?.code === 'ECONNABORTED' || ex?.code === 'ETIMEDOUT';

// A timed-out request has an unknown outcome on the server: callers must not treat it as a plain failure.
const failureOf = (ex) => (isTimeout(ex)
    ? { status: false, timedOut: true, message: 'Request timed out' }
    : { status: false, message: ex.response?.data?.message, error: ex.response?.data });

const authHeaders = (token) => {
    const t = token || getAuth()?.token;
    if (!t) return null;
    return {
        'content-type': 'application/json',
        'auth0-token': t,
        'x-platform': 'adminWeb'
    };
};

export const postWithAuth = (url, entity, token = '', config = {}) => new Promise((resolve, _reject) => {
    const headers = authHeaders(token);
    if (!headers) { resolve(UNAUTH); return; }
    axios
        .post(url, entity, { headers, timeout: DEFAULT_TIMEOUT_MS, ...config })
        .then((response) => {
            if (response && response.data) {
                resolve({ status: true, data: response.data });
            }
        })
        .catch((ex) => {
            if (axios.isCancel(ex)) {
                resolve({ status: false, canceled: true });
                return;
            }
            resolve(failureOf(ex));
        });
});

export const getWithAuth = (url, token = '', options = {}) => {
    const headers = authHeaders(token);
    return new Promise((resolve, _reject) => {
        if (!headers) { resolve(UNAUTH); return; }
        axios
            .get(url, { headers, timeout: DEFAULT_TIMEOUT_MS, ...options })
            .then((response) => {
                if (response && response.data) {
                    resolve({ status: true, data: response.data });
                }
            })
            .catch((ex) => {
                resolve(failureOf(ex));
            });
    });
};

export const getWithOutAuth = (url, options = {}) => {
    const headers = {
        'content-type': 'application/json',
        'x-platform': 'adminWeb'
    };
    return new Promise((resolve, _reject) => {
        axios
            .get(url, { headers, timeout: DEFAULT_TIMEOUT_MS, ...options })
            .then((response) => {
                if (response && response.data) {
                    resolve({ status: true, data: response.data });
                }
            })
            .catch((ex) => {
                resolve(failureOf(ex));
            });
    });
};

export const deleteWithAuth = (url, data, config = {}) => {
    const headers = authHeaders();
    return new Promise((resolve, _reject) => {
        if (!headers) { resolve(UNAUTH); return; }
        axios
            .delete(url, { data, headers, timeout: DEFAULT_TIMEOUT_MS, ...config })
            .then((response) => {
                if (response && response.data) {
                    resolve({ status: true, data: response.data });
                }
            })
            .catch((ex) => {
                resolve(failureOf(ex));
            });
    });
};

export const putWithAuth = (url, data, config = {}) => {
    const headers = authHeaders();
    return new Promise((resolve, _reject) => {
        if (!headers) { resolve(UNAUTH); return; }
        axios
            .put(url, data, { headers, timeout: DEFAULT_TIMEOUT_MS, ...config })
            .then((response) => {
                if (response && response.data) {
                    resolve({ status: true, data: response.data });
                }
            })
            .catch((ex) => {
                resolve(failureOf(ex));
            });
    });
};

export const patchWithAuth = (url, data, config = {}) => {
    const headers = authHeaders();
    return new Promise((resolve, _reject) => {
        if (!headers) { resolve(UNAUTH); return; }
        axios
            .patch(url, data, { headers, timeout: DEFAULT_TIMEOUT_MS, ...config })
            .then((response) => {
                if (response && response.data) {
                    resolve({ status: true, data: response.data });
                }
            })
            .catch((ex) => {
                resolve(failureOf(ex));
            });
    });
};

export const postWithOutAuth = (url, entity, config = {}) => new Promise((resolve, _reject) => {
    const headers = {
        'content-type': 'application/json',
        'x-platform': 'adminWeb'
    };
    axios
        .post(url, entity, { headers, timeout: DEFAULT_TIMEOUT_MS, ...config })
        .then((response) => {
            if (response && response.data) {
                resolve({ status: true, data: response.data });
            }
        })
        .catch((ex) => {
            if (axios.isCancel(ex)) {
                resolve({ status: false, canceled: true });
                return;
            }
            resolve(failureOf(ex));
        });
});
